import type ViewType from "~/@types/viewType";
import type { ProductionMaterialEventDetailSummaryFragment } from "~/gql/queries/produce/productionEventsManagement.generated";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	CreateProductionMaterialEventInput,
	CreateProductionSpaceEventInput,
	CreateProductionTaskEventInput,
	ProductionMaterialEventStatus,
	ProductionSpaceEventDetailDocument,
	ProductionSpaceEventStatus,
	ProductionTaskEventDetailDocument,
	ProductionTaskEventStatus
} from "~/gql/graphqlBase.generated";

export interface SelectedMaterialEvent {
	name: string;
	productionMaterialEventId: string;
	status: ProductionMaterialEventStatus;
}

export interface SelectedSpaceEvent {
	name: string;
	productionSpaceEventId: string;
	status: ProductionSpaceEventStatus;
}

export type SelectedMaterialEventDetail = Pick<
	ProductionMaterialEventDetailSummaryFragment,
	| "description"
	| "materialItemId"
	| "multiple"
	| "productionMaterialEventDetailId"
	| "productionMaterialEventId"
	| "triggerId"
>;

export type SelectedSpaceEventDetail = Pick<
	ProductionSpaceEventDetailDocument,
	| "description"
	| "duration"
	| "productionSpaceCategoryId"
	| "productionSpaceEventDetailId"
	| "productionSpaceEventId"
	| "productionSpaceTypeId"
	| "triggerId"
>;

export type SelectedTaskEventDetail = Pick<
	ProductionTaskEventDetailDocument,
	| "description"
	| "materialItemId"
	| "multiple"
	| "productionTaskEventDetailId"
	| "productionTaskEventId"
	| "sequence"
	| "taskEventTypeId"
	| "triggerId"
	| "unitsPerHour"
>;

export interface SelectedTaskEvent {
	name: string;
	productionTaskEventId: string;
	status: ProductionTaskEventStatus;
}

interface EventsManagementState {
	createProductionMaterialEventInput?: CreateProductionMaterialEventInput;
	createProductionSpaceEventInput?: CreateProductionSpaceEventInput;
	createProductionTaskEventInput?: CreateProductionTaskEventInput;
	eventListRevision: number;
	materialEventFormOpen: boolean;
	selectedMaterialEvent?: SelectedMaterialEvent;
	selectedMaterialEventDetail?: SelectedMaterialEventDetail;
	selectedSpaceEvent?: SelectedSpaceEvent;
	selectedSpaceEventDetail?: SelectedSpaceEventDetail;
	selectedTaskEvent?: SelectedTaskEvent;
	selectedTaskEventDetail?: SelectedTaskEventDetail;
	spaceEventFormOpen: boolean;
	taskEventFormOpen: boolean;
	viewType: ViewType;
}

const initialState: EventsManagementState = {
	eventListRevision: 0,
	materialEventFormOpen: false,
	spaceEventFormOpen: false,
	taskEventFormOpen: false,
	viewType: "card"
};

export const eventsManagementSlice = createSlice({
	initialState,
	name: "eventsManagement",
	reducers: {
		bumpEventListRevision: (state) => {
			state.eventListRevision += 1;
		},
		clearDeletedMaterialEvent: (state, action: PayloadAction<string>) => {
			if (state.selectedMaterialEvent?.productionMaterialEventId !== action.payload) return;
			state.selectedMaterialEvent = undefined;
			state.selectedMaterialEventDetail = undefined;
			state.materialEventFormOpen = false;
		},
		clearMaterialEvent: (state) => {
			state.selectedMaterialEvent = undefined;
		},
		clearMaterialEventDetail: (state) => {
			state.selectedMaterialEventDetail = undefined;
		},
		clearSpaceEvent: (state) => {
			state.selectedSpaceEvent = undefined;
		},
		clearSpaceEventDetail: (state) => {
			state.selectedSpaceEventDetail = undefined;
		},
		clearTaskEvent: (state) => {
			state.selectedTaskEvent = undefined;
		},
		clearTaskEventDetail: (state) => {
			state.selectedTaskEventDetail = undefined;
		},
		closeMaterialEventForm: (state) => {
			state.materialEventFormOpen = false;
		},
		closeSpaceEventForm: (state) => {
			state.spaceEventFormOpen = false;
		},
		closeTaskEventForm: (state) => {
			state.taskEventFormOpen = false;
		},
		openMaterialEventForm: (state) => {
			state.materialEventFormOpen = true;
		},
		openSpaceEventForm: (state) => {
			state.spaceEventFormOpen = true;
		},
		openTaskEventForm: (state) => {
			state.taskEventFormOpen = true;
		},
		setCreateProductionMaterialEventInput: (
			state,
			action: PayloadAction<CreateProductionMaterialEventInput | null>
		) => {
			if (action.payload === null) {
				state.createProductionMaterialEventInput = undefined;
				return;
			}
			state.createProductionMaterialEventInput = { ...action.payload };
		},
		setCreateProductionSpaceEventInput: (state, action: PayloadAction<CreateProductionSpaceEventInput | null>) => {
			if (action.payload === null) {
				state.createProductionSpaceEventInput = undefined;
				return;
			}
			state.createProductionSpaceEventInput = { ...action.payload };
		},
		setCreateProductionTaskEventInput: (state, action: PayloadAction<CreateProductionTaskEventInput | null>) => {
			if (action.payload === null) {
				state.createProductionTaskEventInput = undefined;
				return;
			}
			state.createProductionTaskEventInput = { ...action.payload };
		},
		setSelectedMaterialEvent: (state, action: PayloadAction<SelectedMaterialEvent | undefined>) => {
			state.selectedMaterialEvent = action.payload;
		},
		setSelectedMaterialEventDetail: (state, action: PayloadAction<SelectedMaterialEventDetail | undefined>) => {
			state.selectedMaterialEventDetail = action.payload;
		},
		setSelectedSpaceEvent: (state, action: PayloadAction<SelectedSpaceEvent | undefined>) => {
			state.selectedSpaceEvent = action.payload;
		},
		setSelectedSpaceEventDetail: (state, action: PayloadAction<SelectedSpaceEventDetail | undefined>) => {
			state.selectedSpaceEventDetail = action.payload;
		},
		setSelectedTaskEvent: (state, action: PayloadAction<SelectedTaskEvent | undefined>) => {
			state.selectedTaskEvent = action.payload;
		},
		setSelectedTaskEventDetail: (state, action: PayloadAction<SelectedTaskEventDetail | undefined>) => {
			state.selectedTaskEventDetail = action.payload;
		},
		setViewType: (state, action: PayloadAction<ViewType>) => {
			state.viewType = action.payload;
		},
		updateCreateProductionMaterialEventInput: (
			state,
			action: PayloadAction<Partial<CreateProductionMaterialEventInput> | null>
		) => {
			if (action.payload === null) {
				state.createProductionMaterialEventInput = undefined;
				return;
			}
			if (!state.createProductionMaterialEventInput) return;
			state.createProductionMaterialEventInput = {
				...state.createProductionMaterialEventInput,
				...action.payload
			};
		},
		updateCreateProductionSpaceEventInput: (
			state,
			action: PayloadAction<Partial<CreateProductionSpaceEventInput> | null>
		) => {
			if (action.payload === null) {
				state.createProductionSpaceEventInput = undefined;
				return;
			}
			if (!state.createProductionSpaceEventInput) return;
			state.createProductionSpaceEventInput = {
				...state.createProductionSpaceEventInput,
				...action.payload
			};
		},
		updateCreateProductionTaskEventInput: (
			state,
			action: PayloadAction<Partial<CreateProductionTaskEventInput> | null>
		) => {
			if (action.payload === null) {
				state.createProductionTaskEventInput = undefined;
				return;
			}
			if (!state.createProductionTaskEventInput) return;
			state.createProductionTaskEventInput = {
				...state.createProductionTaskEventInput,
				...action.payload
			};
		},
		updateSelectedMaterialEventName: (state, action: PayloadAction<string>) => {
			if (state.selectedMaterialEvent) {
				state.selectedMaterialEvent = {
					...state.selectedMaterialEvent,
					name: action.payload
				};
			}
		},
		updateSelectedMaterialEventStatus: (state, action: PayloadAction<ProductionMaterialEventStatus>) => {
			if (state.selectedMaterialEvent) {
				state.selectedMaterialEvent = {
					...state.selectedMaterialEvent,
					status: action.payload
				};
			}
		},
		updateSelectedSpaceEventName: (state, action: PayloadAction<string>) => {
			if (state.selectedSpaceEvent) {
				state.selectedSpaceEvent = {
					...state.selectedSpaceEvent,
					name: action.payload
				};
			}
		},
		updateSelectedSpaceEventStatus: (state, action: PayloadAction<ProductionSpaceEventStatus>) => {
			if (state.selectedSpaceEvent) {
				state.selectedSpaceEvent = {
					...state.selectedSpaceEvent,
					status: action.payload
				};
			}
		},
		updateSelectedTaskEventName: (state, action: PayloadAction<string>) => {
			if (state.selectedTaskEvent) {
				state.selectedTaskEvent = {
					...state.selectedTaskEvent,
					name: action.payload
				};
			}
		},
		updateSelectedTaskEventStatus: (state, action: PayloadAction<ProductionTaskEventStatus>) => {
			if (state.selectedTaskEvent) {
				state.selectedTaskEvent = {
					...state.selectedTaskEvent,
					status: action.payload
				};
			}
		}
	}
});

export const {
	bumpEventListRevision,
	clearDeletedMaterialEvent,
	clearMaterialEvent,
	clearMaterialEventDetail,
	clearSpaceEvent,
	clearSpaceEventDetail,
	clearTaskEvent,
	clearTaskEventDetail,
	closeMaterialEventForm,
	closeSpaceEventForm,
	closeTaskEventForm,
	openMaterialEventForm,
	openSpaceEventForm,
	openTaskEventForm,
	setCreateProductionMaterialEventInput,
	setCreateProductionSpaceEventInput,
	setCreateProductionTaskEventInput,
	setSelectedMaterialEvent,
	setSelectedMaterialEventDetail,
	setSelectedSpaceEvent,
	setSelectedSpaceEventDetail,
	setSelectedTaskEvent,
	setSelectedTaskEventDetail,
	setViewType,
	updateCreateProductionMaterialEventInput,
	updateCreateProductionSpaceEventInput,
	updateCreateProductionTaskEventInput,
	updateSelectedMaterialEventName,
	updateSelectedMaterialEventStatus,
	updateSelectedSpaceEventName,
	updateSelectedSpaceEventStatus,
	updateSelectedTaskEventName,
	updateSelectedTaskEventStatus
} = eventsManagementSlice.actions;

export default eventsManagementSlice.reducer;
